import { Component, OnInit, OnDestroy } from '@angular/core';
import { ActivatedRoute, ParamMap, Router } from '@angular/router';
import { ActivityService, Activity, GradedActivity } from '../shared/activity.service';
import { UntypedFormGroup, UntypedFormControl, Validators } from '@angular/forms';
import { CouncilService } from '../shared/council.service';
import { Council } from '../shared/data/council.data';
import { ProgramGroupService } from '../shared/program-group.service';
import { MatLegacySnackBar as MatSnackBar } from '@angular/material/legacy-snack-bar';
import { coerceBooleanProperty } from '@angular/cdk/coercion';
import { Subscription } from 'rxjs';
import { GoogleAnalyticsService } from '../shared/google-analytics.service';
import { AuthService } from '../shared/auth.service';

@Component({
  selector: 'app-grade-activity',
  templateUrl: './grade-activity.component.html',
  styleUrls: ['./grade-activity.component.css']
})
export class GradeActivityComponent implements OnInit, OnDestroy {

  isSubmitting = false;
  isCouncilChoice = false;
  isAllowedToGrade = false;
  activity;
  activityData;
  originalGradedActivity: GradedActivity;
  council: Council;
  gradeActivityForm: UntypedFormGroup;
  fromPage: string | undefined;
  activityStatuses: ActivityStatus[] = [
    {
      id: 'approved',
      displayName: 'Approved'
    },
    {
      id: 'rejected',
      displayName: 'Rejected'
    },
    {
      id: 'reviewed',
      displayName: 'Reviewed'
    }
  ];

  private subscriptions = new Subscription();

  constructor(private route: ActivatedRoute,
              private router: Router,
              private snackBar: MatSnackBar,
              private activityService: ActivityService,
              private councilService: CouncilService,
              private programGroupService: ProgramGroupService,
              private googleAnalyticsService: GoogleAnalyticsService,
              private authService: AuthService) {
    this.gradeActivityForm = new UntypedFormGroup({
      activityStatus: new UntypedFormControl('', [Validators.required]),
      programDirectorComments: new UntypedFormControl(''),
      isNotable: new UntypedFormControl(false),
      isSupremeFeaturedProgram: new UntypedFormControl(false),
    });
  }

  ngOnInit() {
    this.subscriptions.add(this.route.params.subscribe(params => {
      this.subscriptions.add(this.activityService.getActivity(params.id).subscribe(res => {
        this.activity = {...res.data(), id: res.id};
        
        if(!this.activity.submittedDate && this.activity.createdAt) {
          this.activity.submittedDate = this.activity.createdAt.toDate().toLocaleDateString();
        }

        this.subscriptions.add(this.activityService.getActivityData(params.id).subscribe(activityDataRes => {
          this.activityData = activityDataRes.payload.data();
          if (this.activityData) {
            // Track original data
            this.originalGradedActivity = {
              activityId: this.activity.id,
              activityPoints: this.activityData.activityPoints,
              activityStatus: this.activityData.activityStatus,
              programDirectorComments: this.activityData.programDirectorComments,
              isNotable: coerceBooleanProperty(this.activityData.isNotable),
              isSupremeFeaturedProgram: coerceBooleanProperty(this.activityData.isSupremeFeaturedProgram)
            };

            this.gradeActivityForm.get('programDirectorComments').setValue(this.originalGradedActivity.programDirectorComments);
            this.gradeActivityForm.get('isNotable').setValue(this.originalGradedActivity.isNotable);
            this.gradeActivityForm.get('activityStatus').setValue(this.originalGradedActivity.activityStatus);
            this.gradeActivityForm.get('isSupremeFeaturedProgram')
                    .setValue(this.originalGradedActivity.isSupremeFeaturedProgram);


          }
        }));

        this.council = this.councilService.getCouncilById(Number(this.activity.councilId));

        this.isCouncilChoice = this.activity.activityTypeId.indexOf('council-choice') > 0;

        this.subscriptions.add(this.authService.user.subscribe(user => {
          if (user) {
            this.isAllowedToGrade = user.roles.admin 
                      || (this.activity.programGroupId === 'community' && user.roles.communityDirector)
                      || (this.activity.programGroupId === 'faith' && user.roles.faithDirector)
                      || (this.activity.programGroupId === 'family' && user.roles.familyDirector)
                      || (this.activity.programGroupId === 'life' && user.roles.lifeDirector)
                      || (this.activity.programGroupId === 'membership' && user.roles.membershipDirector);
          }
        }));

        return this.activity;  // TODO: Is this needed?
      }));
    }));

    this.subscriptions.add(this.route.queryParams.subscribe(params => {
      this.fromPage = params.from;
    }));
  }

  ngOnDestroy(): void {
    this.subscriptions.unsubscribe();
  }

  getActivityStatuses() {
    return this.activityStatuses;
  }

  getDioceseName(id: number) {
    return this.councilService.getDioceseById(id);
  }

  getActivityName(activityTypeId: string) {
    return this.programGroupService.getActivityNameById(activityTypeId);
  }

  onSubmit() {
    this.isSubmitting = true;

    const gradedActivity: GradedActivity = {
      activityId: this.activity.id,
      activityStatus: this.gradeActivityForm.get('activityStatus').value,
      programDirectorComments: this.gradeActivityForm.get('programDirectorComments').value,
      isNotable: this.gradeActivityForm.get('isNotable').value,
      isSupremeFeaturedProgram: this.gradeActivityForm.get('isSupremeFeaturedProgram').value
    };

    if (this.originalGradedActivity) {
      this.activityService.updateGradedActivity(this.activity as Activity, gradedActivity, this.originalGradedActivity).then((res) => {
        this.googleAnalyticsService.eventEmitter('activity_grading_updated', 'Activity Grading Updated');
        this.navigateBack();
      }).catch(err => {
        this.snackBar.open('Error saving activity', 'Dismiss', {duration: 5000});
        this.googleAnalyticsService.eventEmitter('error_updating_grading', 'Error Updating Grading', 'exception');
        console.log('Error:', err.message);

        this.isSubmitting = false;
      });
    } else {
      this.activityService.gradeActivity(this.activity as Activity, gradedActivity).then((res) => {
        this.googleAnalyticsService.eventEmitter('activity_graded', 'Activity Graded');
        this.navigateBack();
      }).catch(err => {
        this.snackBar.open('Error saving activity', 'Dismiss', {duration: 5000});
        this.googleAnalyticsService.eventEmitter('error_grading_activity', 'Error Grading Activity', 'exception');
        console.log('Error:', err.message);

        this.isSubmitting = false;
      });
    }
  }

  openCouncilSummary() {
    this.googleAnalyticsService.eventEmitter('open_council_summary', 'Open Council Summary');
    const url = this.router.serializeUrl(this.router.createUrlTree(['council-summary', this.activity.councilId]));
    window.open(url, '_blank');
  }

  navigateBack() {
    if (this.fromPage && this.fromPage === 'council-summary') {
      this.router.navigate(['council-summary', this.activity.councilId]);
    } else {
      this.router.navigate(['/activities']);
    }
  }
}

export interface ActivityStatus {
  id: string;
  displayName: string;
}
